import { createContext, ReactNode, useState } from "react"
import emailjs from "@emailjs/browser"
import { TextureLevel, textureWizardDefinition, WizardStepAnswerResult } from "./WizardStepDefinitions"

export enum WizardSectionId {
  SelectTestType,
  SelectFoodTextureLevel,
  SelectFluidThicknessLevel,
  Questionnaire,
  Outcome
}

interface IQuestionAndAnswer {
  question: string
  answer: string
}

type ReportEmailStatus = "unsent" | "sending" | "succeeded" | "failed"
export const TextureTestWizardContext = createContext({
  section: WizardSectionId.SelectTestType,
  currentStepIndex: undefined as number | undefined,
  testTexture: undefined as TextureLevel | undefined,
  testTextureLabel: undefined as string | undefined,
  foodName: "",
  userEmail: "",
  emailStatus: "unsent" as ReportEmailStatus,
  outcome: undefined as WizardStepAnswerResult | undefined,
  answerRecords: [] as IQuestionAndAnswer[],

  goToSection: (section: WizardSectionId) => {},
  answer: (answerIndex: number) => {},
  setTestTexture: (testTexture: TextureLevel) => {},
  getTextureLabel: (textureLevel: TextureLevel): string => "",
  setFoodName: (name: string) => {},
  setUserEmail: (email: string) => {},
  sendEmail: async (): Promise<void> => {},
  reset: () => {}
})

const textureAbbreviations = new Map([
  [TextureLevel.ST1, "ST1"],
  [TextureLevel.MT2, "MT2"],
  [TextureLevel.MO3, "MO3"],
  [TextureLevel.EX4, "EX4"],
  [TextureLevel.LQ3, "LQ3"],
  [TextureLevel.PU4, "PU4"],
  [TextureLevel.MM5, "MM5"],
  [TextureLevel.SB6, "SB6"],
  [TextureLevel.EC7, "EC7"]
])

const textureLabels = new Map([
  [TextureLevel.ST1, `${textureAbbreviations.get(TextureLevel.ST1)} - Slightly Thick`],
  [TextureLevel.MT2, `${textureAbbreviations.get(TextureLevel.MT2)} - Mildly Thick`],
  [TextureLevel.MO3, `${textureAbbreviations.get(TextureLevel.MO3)} - Moderately Thick`],
  [TextureLevel.EX4, `${textureAbbreviations.get(TextureLevel.EX4)} - Extremely Thick`],
  [TextureLevel.LQ3, `${textureAbbreviations.get(TextureLevel.LQ3)} - Liquidised`],
  [TextureLevel.PU4, `${textureAbbreviations.get(TextureLevel.PU4)} - Pureed`],
  [TextureLevel.MM5, `${textureAbbreviations.get(TextureLevel.MM5)} - Minced and Moist`],
  [TextureLevel.SB6, `${textureAbbreviations.get(TextureLevel.SB6)} - Soft and Bite-sized`],
  [TextureLevel.EC7, `${textureAbbreviations.get(TextureLevel.EC7)} - Easy to Chew`]
])

export const TextureTestWizardContextProvider = (props: { children?: ReactNode }) => {
  const [section, setSection] = useState(WizardSectionId.SelectTestType)
  const [currentStepIndex, setCurrentStepIndex] = useState(undefined as number | undefined)
  const [testTexture, setTestTexture] = useState(undefined as TextureLevel | undefined)
  const [foodName, setFoodName] = useState("")
  const [userEmail, setUserEmail] = useState("")
  const [emailStatus, setEmailStatus] = useState("unsent" as ReportEmailStatus)
  const [outcome, setOutcome] = useState(undefined as WizardStepAnswerResult | undefined)
  const [answerRecords, setAnswerRecords] = useState([] as IQuestionAndAnswer[])

  const testTextureLabel = testTexture ? textureLabels.get(testTexture) : undefined
  const testTextureAbbr = testTexture ? textureAbbreviations.get(testTexture) : undefined
  const currentStep =
    testTexture !== undefined && currentStepIndex !== undefined
      ? textureWizardDefinition.paths.get(testTexture)![currentStepIndex]
      : undefined

  const goToSection = (s: WizardSectionId) => {
    setSection(s)
    setCurrentStepIndex(s === WizardSectionId.Questionnaire ? 0 : undefined)
  }

  const answer = (answerIndex: number) => {
    if (!currentStep) return

    const answer = currentStep.answers[answerIndex]
    switch (answer.result) {
      case WizardStepAnswerResult.Proceed:
        setCurrentStepIndex(answer.nextStepIndex)
        setAnswerRecords([...answerRecords, { question: currentStep.question, answer: answer.label }])
        break
      default:
        setSection(WizardSectionId.Outcome)
        setOutcome(answer.result)
        break
    }
  }

  const sendEmail = async () => {
    try {
      setEmailStatus("sending")

      let message = answerRecords.reduce(
        (agg, item) => (agg += `<li>${item.question}: <strong>${item.answer}</strong></li>`),
        "<ul style='padding: 12px; font-style: italic; font-size: 11pt;'>"
      )
      message += `</ul><br/><strong>${foodName}</strong> meets the requirements for <strong>${testTextureLabel}</strong>`

      await emailjs.send(
        "service_k5wx0pr",
        "template_8keifj9",
        {
          email: userEmail,
          test_name: `${testTextureAbbr} - ${foodName}`,
          message: message
        },
        "user_xdhGoAN6UrW48nwLbIZIS"
      )

      setEmailStatus("succeeded")
    } catch {
      setEmailStatus("failed")
    }
  }

  const reset = () => {
    setSection(WizardSectionId.SelectTestType)
    setTestTexture(undefined)
    setFoodName("")
    setUserEmail("")
    setEmailStatus("unsent")
    setAnswerRecords([])
  }

  return (
    <TextureTestWizardContext.Provider
      value={{
        section,
        currentStepIndex,
        testTexture,
        foodName,
        userEmail,
        emailStatus,
        testTextureLabel,
        outcome,
        answerRecords,
        goToSection,
        answer,
        setTestTexture,
        getTextureLabel: (level: TextureLevel) => textureLabels.get(level)!,
        setFoodName,
        setUserEmail,
        sendEmail,
        reset
      }}>
      {props.children}
    </TextureTestWizardContext.Provider>
  )
}
